import { HTMLAttributes, KeyboardEvent, ReactNode, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { Stepper } from '../../../components/Buttons/Stepper';
import { CustomInput } from '../../../components/Input/CustomInput';
import { SecondaryButton } from '../../../components/Buttons/SecondaryButton';
import { sanitizeInput } from '../../../components/Input/IncrementNumberInput';

export interface LogoQuantitiesColumnProps extends HTMLAttributes<HTMLElement> {
    title: string
    actionLabel?: string
    onAction: () => void
    onUpload?: () => void
    /** Rendered directly below the rows, e.g. a totals line. */
    summary?: ReactNode
    /** Cap the row list height and scroll when there are many rows. */
    scrollable?: boolean
    /** Ref to the row-list container (used to focus the newest input). */
    rowsRef?: Ref<HTMLDivElement>
}

export function LogoQuantitiesColumn({ title, onAction, onUpload, actionLabel, summary, scrollable, rowsRef, children }: LogoQuantitiesColumnProps) {
    const { t } = useTranslation();
    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-row justify-between">
                <div className={'uppercase inline pr-1'}>{title}</div>
                <div className={'uppercase inline pr-1 mr-20'}>{t("orderCreate.qty")}</div>
            </div>
            <div
                ref={rowsRef}
                className={`flex flex-col gap-2 ${scrollable ? 'max-h-60 overflow-y-auto pr-1' : ''}`}
            >
                {children}
            </div>
            {summary}
            <SecondaryButton className="w-full" onClick={onAction}>{actionLabel || t("add")}</SecondaryButton>
            <SecondaryButton className="w-full" onClick={onUpload}>{t("orderCreate.uploadExcelFile")}</SecondaryButton>
        </div>
    );
}

export interface LogoQuantityRowProps {
    label: string,
    onLabelChange: (value: string) => void
    quantity: number
    onQuantityChange: (quantity: number) => void
    isNumeric?: Boolean
    placeholder?: string
    /** Called when the user presses Enter in the label input (e.g. add a new row). */
    onEnter?: () => void
    /** When provided, renders a small delete cross that removes this row. */
    onRemove?: () => void
}

export function LogoQuantityRow({ label, onLabelChange, quantity, onQuantityChange, isNumeric, placeholder, onEnter, onRemove }: LogoQuantityRowProps) {
    const { t } = useTranslation();

    const onNumberLabelChange = (value: string) => {
        if (!isNumeric) return onLabelChange(value)
        else {
            const number = parseInt(value)
            if (!isNaN(number))
                onLabelChange(number.toString())
        }
    }

    const onNameLabelChange = (value: string) => {
        // Allow only letters and common characters, disallow numbers for name logos
        const filteredValue = value.replace(/[0-9]/g, '');
        onLabelChange(filteredValue);
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (isNumeric) sanitizeInput(e);
        if (e.key === 'Enter' && onEnter) {
            // Trigger the same action as the add button; never submit/close the dialog.
            e.preventDefault();
            onEnter();
        }
    }

    return (
        <div className="flex flex-row gap-2 items-center justify-between">
            <CustomInput
                onKeyDown={handleKeyDown}
                type={isNumeric ? 'number' : 'text'}
                className="h-[32px]"
                value={label}
                onChange={(e) => isNumeric ? onNumberLabelChange(e.target.value) : onNameLabelChange(e.target.value)}
                placeholder={!isNumeric ? placeholder : undefined}
            />
            <Stepper className="h-8" onValueChange={onQuantityChange} min={0} value={quantity} />
            {onRemove && (
                <button
                    type="button"
                    onClick={onRemove}
                    aria-label={t("orderCreate.removeNameRow")}
                    title={t("orderCreate.removeNameRow")}
                    className="shrink-0 px-1 text-gray-400 hover:text-red-500"
                >
                    <FontAwesomeIcon icon={faXmark} size="sm" />
                </button>
            )}
        </div>
    )
}
